/*
 * File: elmoApi.ts
 * Project: rosita
 * -----
 * Last Modified: 24 Feb 2022 11:31:37 pm
 * -----
 * Copyright (c) 2022 Elegant Software Solutions All rights reserved.
 */

import { BaseQueryFn, createApi, FetchArgs, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import lodash from 'lodash';
import { ApiMultipleResponse } from 'models/ApiModels';
import { Attachment, CreateAttachmentPayload, DeleteAttachmentPayload } from 'models/Attachment';
import { DeploymentRequest, DeploymentRequestParams, DeploymentRequestPayload, DeploymentStatuses } from 'models/DeploymentRequest';
import { ReduxState } from 'redux/store';

/* ******************** Base Query ******************** */
const baseUrl = process.env.REACT_APP_ELMO_BASE_URL || '';
const functionsKey = process.env.REACT_APP_API_HOST_KEY_ELMO || '';

export const elmoBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (args, api, extraOptions) => {
  const divisionId = (api.getState() as ReduxState).app.acuityContext?.selectedCustomer.id;
  const isApiVersion = typeof args !== 'string' && args.url === '/diagnostics/version';

  if (!divisionId && !isApiVersion) {
    return {
      error: {
        status: 400,
        statusText: 'Bad Request',
        data: 'No division ID received'
      }
    };
  }

  const urlEnd = typeof args === 'string' ? args : args.url;
  const adjustedUrl = isApiVersion ? args.url : `divisions/${divisionId}/${urlEnd}`;
  const adjustedArgs = typeof args === 'string' ? adjustedUrl : { ...args, url: adjustedUrl };

  return fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as ReduxState).app.accessToken;

      if (token) {
        headers.set('authorization', `Bearer ${token}`);
        headers.set('x-functions-key', functionsKey);
        headers.set('Content-Type', 'application/json');
      }

      return headers;
    }
  })(adjustedArgs, api, extraOptions);
};

export const elmoApi = createApi({
  reducerPath: 'elmoApi',
  baseQuery: elmoBaseQuery,
  tagTypes: ['DeploymentRequests'],
  endpoints: (builder) => ({
    getDeploymentRequests: builder.query<ApiMultipleResponse<DeploymentRequest>, DeploymentRequestParams>({
      providesTags: ['DeploymentRequests'],
      query: (params) => ({
        url: 'deploymentRequests',
        params: ((): Partial<DeploymentRequestParams> => {
          const filteredObject = lodash.pickBy(params, (v) => v !== undefined);

          if (params.statuses && params.statuses.length && !params.statuses.includes(DeploymentStatuses.submitted)) {
            filteredObject['statuses'] = [DeploymentStatuses.submitted, ...params.statuses];
          }

          return filteredObject;
        })()
      }),
      onQueryStarted: async (_arg, _api) => {
        try {
          await _api.queryFulfilled;
          const { originalArgs } = _api.getCacheEntry();
          const { queries } = _api.getState().elmoApi;
          const lastQueryArgs = { ...originalArgs, offset: _arg.offset - 25 };
          const lastQueryData = Object.values(queries).find((value) => lodash.isEqual(value?.originalArgs, lastQueryArgs));

          if (lastQueryData) {
            _api.dispatch(
              elmoApi.util.updateQueryData('getDeploymentRequests', _arg, (draft) => {
                draft.data.unshift(...(lastQueryData.data as any).data);
              })
            );
          }
        } catch (error) {
          console.log(error);
        }
      }
    }),
    getDeploymentRequest: builder.query<DeploymentRequest, string>({
      query: (deploymentRequestId) => ({
        url: `deploymentRequests/${deploymentRequestId}`
      })
    }),
    createDeploymentRequest: builder.mutation<DeploymentRequest, DeploymentRequestPayload>({
      invalidatesTags: ['DeploymentRequests'],
      query: (data) => ({
        url: 'deploymentRequests',
        method: 'POST',
        body: data
      })
    }),
    updateDeploymentRequest: builder.mutation<DeploymentRequest, DeploymentRequest>({
      query: (data) => ({
        url: `deploymentRequests/${data.id}`,
        method: 'PUT',
        body: data
      })
    }),
    deleteDeploymentRequest: builder.mutation<void, string>({
      invalidatesTags: ['DeploymentRequests'],
      query: (deploymentRequestId) => ({
        url: `deploymentRequests/${deploymentRequestId}`,
        method: 'DELETE'
      })
    }),
    recoverDeploymentRequest: builder.mutation<DeploymentRequest, string>({
      query: (deploymentRequestId) => ({
        url: `deploymentRequests/${deploymentRequestId}/recover`,
        method: 'GET'
      }),
      invalidatesTags: ['DeploymentRequests']
    }),
    createAttachment: builder.mutation<Attachment, CreateAttachmentPayload>({
      query: ({ deploymentRequestId, file }) => ({
        url: `deploymentRequests/${deploymentRequestId}/attachments`,
        method: 'POST',
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        data: file
      })
    }),
    deleteAttachment: builder.mutation<Attachment, DeleteAttachmentPayload>({
      query: ({ deploymentRequestId, fileName }) => ({
        url: `deploymentRequests/${deploymentRequestId}/attachments/${fileName}`,
        method: 'DELETE'
      })
    }),
    getVersion: builder.query<string, void>({
      query: () => ({
        url: '/diagnostics/version',
        responseHandler: (response): Promise<string> => response.text()
      })
    })
  })
});

export const {
  useGetDeploymentRequestsQuery,
  useCreateAttachmentMutation,
  useCreateDeploymentRequestMutation,
  useDeleteAttachmentMutation,
  useDeleteDeploymentRequestMutation,
  useGetDeploymentRequestQuery,
  useRecoverDeploymentRequestMutation,
  useUpdateDeploymentRequestMutation,
  useGetVersionQuery
} = elmoApi;
